
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';
/**
 * Secure Allergy Service
 * Uses backend API endpoints instead of direct Firestore access
 */
class AllergyService {
  
  /**
   * Get all allergy metadata documents
   * @param {number} limitCount - Maximum number of documents to return
   * @returns {Promise<Array>} Array of allergy metadata documents
   */
  async getAllAllergyMetadata(limitCount = 100) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/clinical/allergy-metadata?limit=${limitCount}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.allergies || [];
    } catch (error) {
      console.error('Error fetching allergy metadata:', error);
      throw error;
    }
  }

  /**
   * Search allergy metadata by drug name
   * @param {string} searchTerm - Search term for drug name
   * @param {number} limitCount - Maximum number of results
   * @returns {Promise<Array>} Array of matching allergy metadata
   */
  async searchAllergyMetadata(searchTerm, limitCount = 50) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/clinical/allergy-metadata/search?term=${encodeURIComponent(searchTerm)}&limit=${limitCount}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.allergies || [];
    } catch (error) {
      console.error('Error searching allergy metadata:', error);
      throw error;
    }
  }

  /**
   * Get patient allergies
   * @param {string} patientId - Patient ID
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Array>} Array of patient allergies
   */
  async getPatientAllergies(patientId, facilityId) {
    try {
      const token = await getAuthToken();
      const params = new URLSearchParams();
      
      if (facilityId) params.append('facilityId', facilityId);
      
      const response = await axios.get(
        `${API_BASE}/clinical/patients/${patientId}/allergies?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.allergies;
    } catch (error) {
      console.error('Error getting patient allergies:', error);
      throw error;
    }
  }

  /**
   * Add allergy to patient
   * @param {string} patientId - Patient ID
   * @param {Object} allergyData - Allergy data
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Created allergy record
   */
  async addPatientAllergy(patientId, allergyData, facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/clinical/patients/${patientId}/allergies`,
        {
          ...allergyData,
          facilityId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error adding patient allergy:', error);
      throw error;
    }
  }

  /**
   * Update patient allergy
   * @param {string} patientId - Patient ID
   * @param {string} allergyId - Allergy ID
   * @param {Object} updateData - Update data
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Updated allergy record
   */
  async updatePatientAllergy(patientId, allergyId, updateData, facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.put(
        `${API_BASE}/clinical/patients/${patientId}/allergies/${allergyId}`,
        {
          ...updateData,
          facilityId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error updating patient allergy:', error);
      throw error;
    }
  }

  /**
   * Delete patient allergy
   * @param {string} patientId - Patient ID
   * @param {string} allergyId - Allergy ID
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Deletion result
   */
  async deletePatientAllergy(patientId, allergyId, facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.delete(
        `${API_BASE}/clinical/patients/${patientId}/allergies/${allergyId}?facilityId=${facilityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error deleting patient allergy:', error);
      throw error;
    }
  }

  /**
   * Get allergy by ID
   * @param {string} allergyId - Allergy ID
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Allergy record
   */
  async getAllergyById(allergyId, facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/clinical/allergies/${allergyId}?facilityId=${facilityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.allergy;
    } catch (error) {
      console.error('Error getting allergy by ID:', error);
      throw error;
    }
  }

  /**
   * Get drug allergies from allergy metadata
   * @param {string} drugName - Drug name to search for
   * @returns {Promise<Array>} Array of drug allergy info
   */
  async getDrugAllergies(drugName) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/clinical/allergy-metadata/drug/${encodeURIComponent(drugName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.allergies;
    } catch (error) {
      console.error('Error getting drug allergies:', error);
      throw error;
    }
  }

  /**
   * Check for drug interactions with patient allergies
   * @param {string} patientId - Patient ID
   * @param {string} drugName - Drug name to check
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Interaction check result
   */
  async checkDrugInteractions(patientId, drugName, facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/clinical/patients/${patientId}/allergy-interactions`,
        {
          drugName,
          facilityId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error checking drug interactions:', error);
      throw error;
    }
  }

  /**
   * Batch import allergy metadata
   * @param {Array} allergyData - Array of allergy metadata objects
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Import result
   */
  async batchImportAllergyMetadata(allergyData, facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/clinical/allergy-metadata/batch-import`,
        {
          allergies: allergyData,
          facilityId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error importing allergy metadata:', error);
      throw error;
    }
  }

  /**
   * Get allergy categories
   * @returns {Promise<Array>} Array of allergy categories
   */
  async getAllergyCategories() {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/clinical/allergy-categories`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.categories;
    } catch (error) {
      console.error('Error fetching allergy categories:', error);
      // Return empty array as fallback to prevent component errors
      return [];
    }
  }

  /**
   * Get allergy statistics for facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Allergy statistics
   */
  async getAllergyStatistics(facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/clinical/allergy-statistics?facilityId=${facilityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.statistics;
    } catch (error) {
      console.error('Error getting allergy statistics:', error);
      throw error;
    }
  }

  /**
   * Check prescription safety - comprehensive allergy and drug interaction check
   * @param {string} patientId - Patient ID
   * @param {Array<{name: string, rxNormConceptId?: string, dosage?: string}>} medications - Array of medications to check
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Safety check result with warnings
   */
  async checkPrescriptionSafety(patientId, medications, facilityId) {
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/clinical/prescription-safety-check`,
        {
          patientId,
          medications,
          facilityId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error checking prescription safety:', error);
      throw error;
    }
  }
}

// Export singleton instance
const allergyService = new AllergyService();
export default allergyService;
